import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "@/components/shared/Loader";

export default function TicketDetail() {
  const { id } = useParams();
  const { t } = useLang();
  useSEO("Ticket");
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/tickets/${id}`).then((r) => setTicket(r.data)).catch(() => setTicket(false));
  useEffect(() => { load(); }, [id]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setLoading(true);
    try {
      await api.post(`/tickets/${id}/reply`, { message: reply });
      setReply("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (ticket === null) return <Loader full={false} />;
  if (ticket === false) return <p className="text-slate-400">Ticket not found. <Link to="/dashboard/tickets" className="text-brand-cyan">Back</Link></p>;

  return (
    <div className="space-y-6 max-w-3xl">
      <Link to="/dashboard/tickets" className="inline-flex items-center gap-2 text-sm text-brand-cyan hover:underline" data-testid="ticket-back"><ArrowLeft className="h-4 w-4 rtl-mirror" /> {t("support")}</Link>
      <div className="card-surface rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-xl font-bold text-white">{ticket.subject}</h1>
          <span className="rounded-full bg-white/5 px-3 py-1 text-xs capitalize text-slate-300">{ticket.status}</span>
        </div>
      </div>
      <div className="space-y-3" data-testid="ticket-messages">
        {ticket.messages?.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.sender === "user" ? "gradient-brand text-brand-navy" : "card-surface text-slate-200"}`}>
              <p className="text-[10px] uppercase font-mono opacity-70 mb-1">{m.sender === "user" ? "You" : "INVIXA Support"}</p>
              <p className="text-sm whitespace-pre-wrap">{m.message}</p>
              <p className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>
      {ticket.status !== "closed" && (
        <form onSubmit={send} className="card-surface rounded-2xl p-4 flex gap-3 items-end" data-testid="ticket-reply-form">
          <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply..." data-testid="ticket-reply-input" className="flex-1 bg-white/5 border-white/10 resize-none" />
          <Button type="submit" disabled={loading} className="gradient-brand text-brand-navy font-semibold" data-testid="ticket-reply-send"><Send className="h-4 w-4" /></Button>
        </form>
      )}
    </div>
  );
}
